import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { LineChart } from 'lucide-react';
import { api } from '@/api/client';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Alert } from '@/components/ui/Alert';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, SkeletonCard } from '@/components/ui/States';
import { BandTrend } from '@/features/progress/BandTrend';
import { RecommendationCard } from '@/features/progress/RecommendationCard';
import { SkillOverviewCard } from '@/features/progress/SkillOverviewCard';
import { WeakAreaList } from '@/features/progress/WeakAreaList';

export const Route = createFileRoute('/progress')({
  validateSearch: (search: Record<string, unknown>) => ({
    variant: typeof search['variant'] === 'string' ? search['variant'] : undefined,
  }),
  component: ProgressPage,
});

function ProgressPage() {
  const { variant: variantSearch } = Route.useSearch();

  const overviewQuery = useQuery({
    queryKey: ['progress-overview', variantSearch],
    queryFn: () =>
      api.get(
        '/api/v1/progress/overview',
        variantSearch ? { query: { variant: variantSearch } } : {},
      ),
  });

  const weakAreasQuery = useQuery({
    queryKey: ['progress-weak-areas', variantSearch],
    queryFn: () =>
      api.get(
        '/api/v1/progress/weak-areas',
        variantSearch ? { query: { variant: variantSearch } } : {},
      ),
  });

  const trend = useMemo(() => {
    const skills = overviewQuery.data?.skills ?? {};
    return Object.values(skills)
      .flatMap((skill) => skill.history)
      .map((point) => ({ submittedAt: point.submittedAt, value: point.score.value }))
      .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
  }, [overviewQuery.data]);

  if (overviewQuery.isPending || weakAreasQuery.isPending) {
    return (
      <div className="grid gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (overviewQuery.isError || weakAreasQuery.isError) {
    return (
      <Alert tone="danger" title="We could not load your progress.">
        Reload the page to try again.
      </Alert>
    );
  }

  const overview = overviewQuery.data;
  const weakAreas = weakAreasQuery.data;
  const skills = Object.keys(overview.skills).sort();
  const variantSlug = overview.variant.slug;

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow={overview.variant.name}
        title="Progress"
        description="What you have scored so far, and what to work on next."
        actions={
          <Link
            to="/practice"
            search={{ variant: variantSlug, section: 'mocks' }}
            className={buttonClassName()}
          >
            Start a test
          </Link>
        }
      />

      <ExamSurfaceDisclaimer text={overview.variant.trademarkDisclaimer} />

      {overview.evidence.attempts === 0 ? (
        <EmptyState
          icon={LineChart}
          title="No scored tests yet"
          description={`Finish one ${overview.variant.name} test and your bands, trend and weak areas appear here.`}
          action={
            <Link
              to="/practice"
              search={{ variant: variantSlug, section: 'mocks' }}
              className={buttonClassName()}
            >
              Start a test
            </Link>
          }
        />
      ) : (
        <>
          {trend.length >= 2 ? (
            <Card padding="lg" className="grid gap-4">
              <h2 className="font-display text-h3">Band over time</h2>
              <BandTrend points={trend} />
            </Card>
          ) : null}

          <section aria-labelledby="progress-skills" className="grid gap-4">
            <h2 id="progress-skills" className="font-display text-h3">
              By skill
            </h2>
            <ul className="grid gap-4 sm:grid-cols-2">
              {skills.map((skill) => {
                const skillOverview = overview.skills[skill];
                if (!skillOverview) return null;
                return (
                  <SkillOverviewCard
                    key={skill}
                    skill={skill}
                    overview={skillOverview}
                    variant={variantSlug}
                  />
                );
              })}
            </ul>
          </section>

          <section aria-labelledby="progress-recommendations" className="grid gap-4">
            <h2 id="progress-recommendations" className="font-display text-h3">
              Recommended next
            </h2>
            {overview.topRecommendations.length === 0 ? (
              <p className="text-body text-muted">
                Nothing to recommend yet. Finish another test and we will suggest what to do.
              </p>
            ) : (
              <ul className="grid gap-4 md:grid-cols-2">
                {overview.topRecommendations.map((recommendation) => (
                  <li key={recommendation.id}>
                    <RecommendationCard recommendation={recommendation} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="progress-weak" className="grid gap-4">
            <h2 id="progress-weak" className="font-display text-h3">
              Weak areas
            </h2>
            <WeakAreaList weakAreas={weakAreas.weakAreas} />
          </section>
        </>
      )}
    </div>
  );
}
